import { Injectable, inject } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';
import { ApiClientService } from './api-client.service';
import type { RoleTerminology, SportDiscipline } from './tenant.service';

export type TenantPublicStatus = 'Active' | 'Suspended' | 'PendingEmailVerification';

export interface TenantPublicBranding {
  readonly logoUrl?: string | null;
  readonly coverPhotoUrl?: string | null;
  readonly primaryColorHex: string;
  readonly accentColorHex?: string | null;
  readonly hideSportDeskMentions: boolean;
}

export interface TenantPublicDto {
  readonly tenantId: string;
  readonly subdomain: string;
  readonly teamName: string;
  readonly discipline: SportDiscipline;
  readonly status: TenantPublicStatus;
  readonly loginUrl: string;
  readonly countryCode: string;
  readonly locale: string;
  readonly timezone: string;
  readonly currency: string;
  readonly branding: TenantPublicBranding;
  readonly terminologyByLocale: Record<string, RoleTerminology>;
  readonly publicSiteEnabled: boolean;
}

@Injectable({ providedIn: 'root' })
export class TenantPublicService {
  private readonly api = inject(ApiClientService);
  private readonly cache = new Map<string, Observable<TenantPublicDto>>();

  getBySubdomainOrId(subdomainOrId: string): Observable<TenantPublicDto> {
    const key = subdomainOrId.toLowerCase();
    const cached = this.cache.get(key);
    if (cached) return cached;
    const request$ = this.api
      .get<TenantPublicDto>(`/api/tenants/${encodeURIComponent(subdomainOrId)}/public`)
      .pipe(shareReplay({ bufferSize: 1, refCount: false }));
    this.cache.set(key, request$);
    return request$;
  }

  invalidate(subdomainOrId?: string): void {
    if (!subdomainOrId) {
      this.cache.clear();
      return;
    }
    this.cache.delete(subdomainOrId.toLowerCase());
  }
}
